import DarkMode from "@mui/icons-material/DarkMode";
import WbSunny from "@mui/icons-material/WbSunny";
import { Box, Button, Typography } from "@mui/material";
import { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { OpenShiftCard } from "../../components/OpenShiftCard/OpenShiftCard";
import { useAuth } from "../../contexts/AuthProvider";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { SHIFT_DAY, SHIFT_NIGHT_FALLBACK } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { RequestResponsesStatusOptions, type ShiftFilterType, ShiftType } from "../../utils/models";

export const OpenShifts: FC = () => {
	const { userInfo } = useAuth();
	const { mergedRequests } = useRequestsContext();
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();
	const [shiftType, setShiftType] = useState<ShiftFilterType>(ShiftType.All);
	const [expandedShifts, setExpandedShifts] = useState<string[]>([]);

	// Check for highlight params in URL
	const highlightDate = searchParams.get("highlight");
	const highlightShiftType = searchParams.get("shift_type");

	// Find shifts to highlight based on URL params
	useEffect(() => {
		if (highlightDate && highlightShiftType && mergedRequests.length > 0) {
			const shiftsToHighlight = mergedRequests.filter((shift) => {
				const dateMatches = shift.startTime.toFormat("yyyy-MM-dd") === highlightDate;
				const typeMatches = shift.type === highlightShiftType;
				return dateMatches && typeMatches;
			});

			// Expand highlighted shifts
			if (shiftsToHighlight.length > 0) {
				const shiftIds = shiftsToHighlight.map((shift) => shift.id);
				setExpandedShifts(shiftIds);

				// Set filter to match the shift type if needed
				if (highlightShiftType === ShiftType.Day || highlightShiftType === ShiftType.Night) {
					setShiftType(highlightShiftType as ShiftFilterType);
				}

				// Log analytics event
				trackEvent("shift_highlighted_from_notification", {
					user_id: userInfo?.id,
					highlight_date: highlightDate,
					highlight_shift_type: highlightShiftType,
					matching_shifts: shiftIds.length,
				});

				// Scroll to the highlighted shift
				setTimeout(() => {
					if (shiftIds.length > 0) {
						const elementId = `shift-card-${shiftIds[0]}`;
						const element = document.getElementById(elementId);
						if (element) {
							element.scrollIntoView({ behavior: "smooth", block: "center" });
						}
					}
				}, 500);
			}
		}
	}, [highlightDate, highlightShiftType, mergedRequests, userInfo?.id]);

	const handleShiftTypeChange = (newShiftType: ShiftFilterType) => {
		trackEvent("open_shifts_filter_changed", {
			user_id: userInfo?.id,
			previous_filter: shiftType,
			new_filter: newShiftType,
		});
		setShiftType(newShiftType);
	};

	const handleExpandClick = (shiftId: string) => {
		const isCurrentlyExpanded = expandedShifts.includes(shiftId);
		trackEvent("open_shifts_expand_toggle", {
			user_id: userInfo?.id,
			shift_id: shiftId,
			is_expanding: !isCurrentlyExpanded,
		});
		setExpandedShifts((prev) => (prev.includes(shiftId) ? prev.filter((id) => id !== shiftId) : [...prev, shiftId]));
	};

	const filteredShifts = mergedRequests.filter((shift) => {
		if (shiftType === ShiftType.All) return true;
		return shift.type === shiftType;
	});

	return (
		<Box
			data-testid="open-shifts-root"
			sx={{
				width: "100%",
				height: "calc(100vh - 50px)",
				display: "flex",
				flexDirection: "column",
				position: "absolute",
				top: "50px",
				left: 0,
				right: 0,
				bottom: 0,
				bgcolor: "#E7E9EF4D",
				overflowY: "auto",
			}}
		>
			<Box sx={{ p: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 1 } }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "stretch",
						width: "100%",
						backgroundColor: "#F1F3F9",
						borderRadius: "20px",
						padding: "2px",
					}}
				>
					<Button
						data-testid="open-shifts-filter-all"
						onClick={() => handleShiftTypeChange(ShiftType.All)}
						sx={{
							flex: 1,
							padding: "4px 8px",
							height: "32px",
							borderRadius: "16px",
							textTransform: "none",
							backgroundColor: shiftType === ShiftType.All ? "#fff" : "transparent",
							color: shiftType === ShiftType.All ? "primary.dark" : "primary.main",
							boxShadow: shiftType === ShiftType.All ? "0px 1px 3px rgba(0, 0, 0, 0.1)" : "none",
							"&:hover": {
								backgroundColor: shiftType === ShiftType.All ? "#fff" : "#E8EAF2",
							},
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: 1,
							fontSize: "0.875rem",
						}}
					>
						<Typography sx={{ fontSize: "inherit" }}>{t("all.day")}</Typography>
					</Button>
					<Button
						data-testid="open-shifts-filter-day"
						onClick={() => handleShiftTypeChange(ShiftType.Day)}
						sx={{
							flex: 1,
							padding: "4px 8px",
							height: "32px",
							borderRadius: "16px",
							textTransform: "none",
							backgroundColor: shiftType === ShiftType.Day ? "#fff" : "transparent",
							color: shiftType === ShiftType.Day ? "primary.dark" : "primary.main",
							boxShadow: shiftType === ShiftType.Day ? "0px 1px 3px rgba(0, 0, 0, 0.1)" : "none",
							"&:hover": {
								backgroundColor: shiftType === ShiftType.Day ? "#fff" : "#E8EAF2",
							},
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: 1,
							fontSize: "0.875rem",
						}}
					>
						<WbSunny sx={{ fontSize: "18px", color: SHIFT_DAY }} />
						<Typography sx={{ fontSize: "inherit" }}>{t("Day")}</Typography>
					</Button>
					<Button
						data-testid="open-shifts-filter-night"
						onClick={() => handleShiftTypeChange(ShiftType.Night)}
						sx={{
							flex: 1,
							padding: "4px 8px",
							height: "32px",
							borderRadius: "16px",
							textTransform: "none",
							backgroundColor: shiftType === ShiftType.Night ? "#fff" : "transparent",
							color: shiftType === ShiftType.Night ? "primary.dark" : "primary.main",
							boxShadow: shiftType === ShiftType.Night ? "0px 1px 3px rgba(0, 0, 0, 0.1)" : "none",
							"&:hover": {
								backgroundColor: shiftType === ShiftType.Night ? "#fff" : "#E8EAF2",
							},
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: 1,
							fontSize: "0.875rem",
						}}
					>
						<DarkMode sx={{ fontSize: "18px", color: SHIFT_NIGHT_FALLBACK }} />
						<Typography sx={{ fontSize: "inherit" }}>{t("Night")}</Typography>
					</Button>
				</Box>
			</Box>

			<Box
				data-testid="open-shifts-list"
				sx={{
					flex: 1,
					overflowY: "auto",
					p: { xs: 2, sm: 3 },
					display: "flex",
					flexDirection: "column",
					gap: { xs: 1, sm: 2 },
					mt: -1,
					pt: 0,
				}}
			>
				{[...filteredShifts]
					.sort((a, b) => {
						// Helper function to get priority of status
						const getStatusPriority = (status: string) => {
							if (!status || status === "") return 0;
							if (status.toLowerCase() === RequestResponsesStatusOptions.PENDING) return 1;
							if (status.toLowerCase() === RequestResponsesStatusOptions.APPROVED) return 2;
							if (status.toLowerCase() === RequestResponsesStatusOptions.CLOSED) return 3;
							if (status.toLowerCase() === RequestResponsesStatusOptions.DECLINED) return 4;
							return 5;
						};

						// First compare by status priority
						const statusA = getStatusPriority(a.status);
						const statusB = getStatusPriority(b.status);
						if (statusA !== statusB) return statusA - statusB;

						// If same status, sort by date
						const dateComparison = a.startTime.valueOf() - b.startTime.valueOf();
						if (dateComparison !== 0) return dateComparison;

						// If same date, sort by shift type (day before night)
						return a.type === ShiftType.Day ? -1 : 1;
					})
					.map((shift) => (
						<OpenShiftCard
							key={shift.id}
							id={`shift-card-${shift.id}`}
							shift={shift}
							isExpanded={expandedShifts.includes(shift.id)}
							onExpandClick={handleExpandClick}
							dateTitle={true}
							isHighlighted={Boolean(
								highlightDate && highlightShiftType && shift.startTime.toFormat("yyyy-MM-dd") === highlightDate && shift.type === highlightShiftType,
							)}
						/>
					))}
			</Box>
		</Box>
	);
};
